import {DateTime} from 'luxon';

import {getCultureCode, getDateMask} from '@/use/culture';

export function formatCultureDate(
  value: string | Date | null | undefined,
  maskType = 'short',
): string {
  if (!value) {
    return '';
  }

  let date: DateTime;

  if (value instanceof Date) {
    date = DateTime.fromJSDate(value).setLocale(getCultureCode());
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    date = DateTime.fromFormat(value, 'yyyy-LL-dd', {locale: getCultureCode()});
  } else {
    date = DateTime.fromISO(value, {zone: 'utc', locale: getCultureCode()}).toLocal();

    if (!date.isValid) {
      date = DateTime.fromSQL(value, {zone: 'utc', locale: getCultureCode()}).toLocal();
    }
  }

  return date.isValid ? date.toFormat(getDateMask(maskType)) : String(value);
}

export function formatCultureNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? new Intl.NumberFormat(getCultureCode()).format(number)
    : String(value);
}

export function formatCulturePhoneNumber(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const sanitizedValue = value.replace(/[^x0-9 .\-+()]/g, '');
  const numbersOnly = value.replace(/[^0-9]/g, '');
  const cultureCode = getCultureCode().toLowerCase();

  if (!['en-us', 'en-ca', 'fr-ca'].includes(cultureCode)) {
    return sanitizedValue;
  }

  switch (numbersOnly.length) {
    case 7:
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
    case 10:
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 6)}-${numbersOnly.slice(6)}`;
    case 11:
      return `${numbersOnly.slice(0, 1)}-${numbersOnly.slice(1, 4)}-${numbersOnly.slice(4, 7)}-${numbersOnly.slice(7)}`;
    default:
      return sanitizedValue;
  }
}

export function serializeCulturePhoneNumber(
  displayedValue: string | null | undefined,
  originalValue: string | null | undefined,
): string | null {
  const normalizedDisplayedValue = displayedValue ?? '';

  // Formatting a value for the form must not turn an unchanged phone number
  // into a company-update request merely because its punctuation changed.
  if (normalizedDisplayedValue === formatCulturePhoneNumber(originalValue)) {
    return originalValue ?? null;
  }

  return normalizedDisplayedValue || null;
}
